import { Button, Divider, TextField, Typography } from "@mui/material";
import { Box, Grid } from "@mui/system";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useBrandProperties } from "../brandPropertiesHook/useBrandProperties";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import { BrandConfigReportEndPointLogic } from "../../../../logic/Partners/DataIntegrations/BrandConfigReportEndPointLogic";
import { useSelector } from "react-redux";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

const BrandPropertiesForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userData = useSelector(selectUserData);
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const brand_shared_uid = searchParams.get("brandSharedUid") as any;
  const brandId = searchParams.get("brandId") as any;
  const step = searchParams.get("step") as any;
  const [updatePropLoader, setUpdatePropLoader] = useState(false);

  const { formattedBrandProperties } = useBrandProperties();
  const [inputProperties, setInputProperties] = useState<any>(() =>
    Array.isArray(formattedBrandProperties) &&
    formattedBrandProperties.length > 0
      ? [...formattedBrandProperties]
      : [],
  );

  // this is for check the initial value with current value of dynamic input
  const [initialBrandProperties] = useState<any>(() =>
    Array.isArray(formattedBrandProperties) &&
    formattedBrandProperties.length > 0
      ? JSON.parse(JSON.stringify(formattedBrandProperties))
      : [],
  );

  // State to track validation errors
  const [validationErrors, setValidationErrors] = useState<{
    [key: number]: string;
  }>({});

  // State to track touched fields
  const [touchedFields, setTouchedFields] = useState<{
    [key: number]: boolean;
  }>({});

  // Check user permissions
  const hasBrandConfigEditPermission = CheckPermissions(
    userData?.data?.products?.partners,
    "data_intregrations",
    "configure_new_brand",
  );

  // Check if any mandatory field is empty
  const hasEmptyMandatoryFields = inputProperties.some((item: any) => {
    const isRequired =
      item.requirementType === "required" ||
      item.requirementType === "conditional_requirement";
    return isRequired && (!item.value || item.value.trim() === "");
  });

  // Function to go back to the previous page
  const backToPreviousPage = (resetPage: boolean = false) => {
    const currentParams = new URLSearchParams(location.search);
    if (resetPage) currentParams.set("page", "0"); // Reset to first page on back
    navigate(
      `/operations/brand_configuration_report?${currentParams.toString()}`,
    );
  };

  // Handle input change
  const handleInputChange = (index: number, newValue: string) => {
    setInputProperties((prevProperties: any) =>
      prevProperties.map((item: any, i: any) =>
        i === index ? { ...item, value: newValue } : item,
      ),
    );

    // Validate field on change if it has been touched
    if (touchedFields[index]) {
      const item = inputProperties[index];
      const error = validateField(item, newValue);
      setValidationErrors((prev) => ({
        ...prev,
        [index]: error,
      }));
    }
  };

  const validateField = (item: any, value: string): string => {
    const isRequired =
      item.requirementType === "required" ||
      item.requirementType === "conditional_requirement";

    // Check if field is required and empty
    if (isRequired && (!value || value.trim() === "")) {
      return `${item.label} is required`;
    }
    if (/\s{2,}/.test(value)) {
      return "Double spaces are not allowed.";
    }
    return "";
  };

  // Validate all fields
  const validateAllFields = (): boolean => {
    const errors: { [key: number]: string } = {};
    let isValid = true;

    inputProperties.forEach((item: any, index: number) => {
      const error = validateField(item, item.value);
      if (error) {
        errors[index] = error;
        isValid = false;
      }
    });

    setValidationErrors(errors);

    // Mark all fields as touched
    const allTouched = inputProperties.reduce(
      (acc: any, _: any, index: number) => {
        acc[index] = true;
        return acc;
      },
      {},
    );
    setTouchedFields(allTouched);

    return isValid;
  };

  // Handle field blur
  const handleFieldBlur = (index: number) => {
    setTouchedFields((prev) => ({
      ...prev,
      [index]: true,
    }));

    // Validate on blur
    const item = inputProperties[index];
    const error = validateField(item, item.value);
    setValidationErrors((prev) => ({
      ...prev,
      [index]: error,
    }));
  };

  // Update placeholder value for pattern type
  function updatePlaceholderValue(
    config: any,
    placeholderKey: any,
    newValue: any,
  ) {
    config.value = newValue;

    // Update placeholder to match
    const placeholderObj = JSON.parse(config.placeholder);
    placeholderObj[placeholderKey] = newValue;
    config.placeholder = JSON.stringify(placeholderObj);

    return config.placeholder;
  }

  // Fetch changed fields those values are changed
  const getChangedProperties = () => {
    const changedProperties = inputProperties.reduce(
      (acc: any[], item: any, index: number) => {
        const initialItem = initialBrandProperties[index];

        if (initialItem && item.value !== initialItem.value) {
          if (item.key_type === "pattern") {
            acc.push({
              property_target: item.property_target,
              value: updatePlaceholderValue(
                item,
                item.placeholderKey,
                item.value,
              ),
              key_name: item.key_name,
            });
          } else {
            acc.push({
              property_target: item.property_target,
              value: item.value,
              key_name: item.key_name,
            });
          }
        }

        return acc;
      },
      [],
    );

    return changedProperties;
  };

  // Update brand properties
  const updateBrandProperties = async () => {
    // Validate all fields
    if (!validateAllFields()) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please fix all validation errors before proceeding.",
          type: "error",
        }),
      );
      return;
    }

    // Check for changes
    const changedFields = getChangedProperties();
    if (changedFields.length === 0) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "No changes detected. Please make changes before saving.",
          type: "warning",
        }),
      );
      return;
    }

    const payload = { properties: changedFields };

    setUpdatePropLoader(true);

    try {
      const response =
        await BrandConfigReportEndPointLogic.updateBrandProperties(
          oauthTokenHeader,
          payload,
          brand_shared_uid,
        );

      const responseStatus = response?.status;

      if (responseStatus !== 200) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong; please try again later..",
            type: "error",
          }),
        );
        return;
      }

      if (responseStatus === 200) {
        const overrideConfiguration = step === "Properties" ? true : false;
        const data = {
          brand_id: brandId,
          override_configuration: overrideConfiguration,
        };

        const res =
          await BrandConfigReportEndPointLogic.postBrandConfigurationProcess(
            oauthTokenHeader,
            data,
          );

        if (res?.status === 200) {
          const successMessage =
            res?.data?.message ||
            "Brand Integration Process has been successfully initiated.";
          backToPreviousPage(true);
          dispatch(
            setDisplayErrorSnackBar({
              message: successMessage,
              type: "success",
            }),
          );
        } else {
          dispatch(
            setDisplayErrorSnackBar({
              message: "Failed to process brand integration.",
              type: "error",
            }),
          );
        }
      }
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        e?.parsedText ||
        e?.response?.data?.message ||
        "Failed to update properties. Please try again.";
      dispatch(
        setDisplayErrorSnackBar({
          message: errorMessage,
          type: "error",
        }),
      );
    } finally {
      setUpdatePropLoader(false);
    }
  };

  if (inputProperties.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="body1" color="textSecondary">
          No properties available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        {inputProperties?.length > 0 &&
          inputProperties.map((item: any, index: number) => (
            <Grid container mb={2} key={item.label}>
              <Grid size={6}>
                <TextField
                  disabled={updatePropLoader}
                  size="medium"
                  color="secondary"
                  fullWidth
                  label={item.label}
                  value={item.value}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onBlur={() => handleFieldBlur(index)}
                  variant="outlined"
                  required={
                    item.requirementType === "required" ||
                    item.requirementType === "conditional_requirement"
                  }
                  error={touchedFields[index] && !!validationErrors[index]}
                  helperText={
                    touchedFields[index] && validationErrors[index]
                      ? validationErrors[index]
                      : item.helperText
                  }
                />
              </Grid>
            </Grid>
          ))}
      </Box>
      <Typography variant="body2" mt={2}>
        Fields marked with * are mandatory.
      </Typography>
      <Divider sx={{ my: 3 }} />

      <Box justifyContent={"flex-end"} display={"flex"} mb={1}>
        <Button
          disabled={updatePropLoader}
          variant="text"
          color="secondary"
          startIcon={<ArrowBackIosIcon style={{ fontSize: "15px" }} />}
          sx={{ mr: 2 }}
          onClick={() => backToPreviousPage()}
        >
          Back to configuration report
        </Button>

        <Button
          data-tracker="633"
          disabled={
            !hasBrandConfigEditPermission ||
            updatePropLoader ||
            hasEmptyMandatoryFields
          }
          variant="outlined"
          color="secondary"
          endIcon={<ArrowForwardIosIcon />}
          onClick={updateBrandProperties}
        >
          Save & Run
        </Button>
      </Box>
    </>
  );
};

export default BrandPropertiesForm;
